import { DocumentReference } from 'firebase/firestore';
import { type Property } from './property';
import { type FeatureOption, FeatureType } from './feature';

export interface Corporation {
	id: string;
	name: string;
	abbv: string;
	tag: string;
	description: string;
	imageURL: string;
	docURL: string;
	propertyRefs: DocumentReference[];
	properties: Property[];
	createdAt?: number;
	updatedAt?: number;
}

export enum CorporationTag {
	C_SEC = 'corsec',
	CONFED = 'confed',
	CREED = 'creed',
	FOFT = 'foft',
	GRAD = 'grad',
	GRAD_FAIL = 'grad-fail',
	ICE = 'ice',
	ICU = 'icu',
	MERCS = 'mercs',
	NERD = 'nerd',
	ROBO = 'robo',
	SAROS = 'saros',
	UNI_NAV = 'uni-nav',
	XERO = 'xero',
}

export const corporationMap: { [key in CorporationTag]: string } = {
	[CorporationTag.C_SEC]: 'Core Security Corporation',
	[CorporationTag.CONFED]: 'Confederation of Planets',
	[CorporationTag.CREED]: 'Corporate Removal of Enterprise Employees by Death',
	[CorporationTag.FOFT]: 'Federation Of Free Traders',
	[CorporationTag.GRAD]: 'Galactic Research and Development',
	[CorporationTag.GRAD_FAIL]: 'Galactic Research and Development - Failure',
	[CorporationTag.ICE]: 'Intergalactic Cybernetics UniCorp',
	[CorporationTag.ICU]: 'Intergalactic Cybernetics UniCorp',
	[CorporationTag.MERCS]: 'Mercenary Expansion Recovery and Capitalization Syndicate',
	[CorporationTag.NERD]: 'Neurological Expansion Research and Development Corporation',
	[CorporationTag.ROBO]: 'Robotic and Bionic Operations',
	[CorporationTag.SAROS]: 'Search and Rescue Operatives',
	[CorporationTag.UNI_NAV]: 'Universal Navigation Corporation',
	[CorporationTag.XERO]: 'Ex-Enterprise Roaming Organics',
};

export const getCorpFullName = (tag: CorporationTag): string => {
	return corporationMap[tag] || corporationMap[CorporationTag.FOFT];
};

export const getCorporationTag = (input: string | undefined): CorporationTag => {
	if (input === undefined) {
		return CorporationTag.FOFT; // Default value if input is undefined
	}

	// Normalize the input to handle case-insensitivity
	const normalizedInput = input.trim().toLowerCase();

	// Check both keys and values of the CorporationTags enum
	for (const tag in CorporationTag) {
		if (
			CorporationTag[tag as keyof typeof CorporationTag].toLowerCase() === normalizedInput ||
			tag.toLowerCase() === normalizedInput
		) {
			return CorporationTag[tag as keyof typeof CorporationTag];
		}
	}

	// If no match is found, return undefined
	return CorporationTag.FOFT;
};

export const corporationTag2Corporation = (tag: CorporationTag): Corporation => {
	const corpName = getCorpFullName(tag);
	const corpAbbreviation = tag.toUpperCase();
	return {
		name: corpName || '',
		abbv: corpAbbreviation || '',
		tag,
	};
};

export const getDefaultCorporation = (): Corporation => {
	return {
		id: '-1',
		name: '',
		abbv: '',
		tag: '',
		description: '',
		imageURL: '',
		docURL: '',
		propertyRefs: [],
		properties: [],
		createdAt: Date.now(),
		updatedAt: Date.now(),
	};
};

export const corporation2FeatureOption = (corp: Corporation): FeatureOption => {
	return {
		id: corp.id,
		name: corp.name,
		type: FeatureType.corp,
		imageURL: corp.imageURL,
		docURL: corp.docURL,
		tag: corp.tag,
		description: corp.description,
		propertyRefs: corp.propertyRefs,
	};
};

export const featureOption2Corporation = (featureOption: FeatureOption): Corporation => {
	return {
		id: featureOption.id,
		name: featureOption.name,
		abbv: '', // Will be preserved from original
		tag: featureOption.tag,
		description: featureOption.description,
		imageURL: featureOption.imageURL,
		docURL: featureOption.docURL,
		propertyRefs: featureOption.propertyRefs,
		properties: [],
	};
};

export const getRankIconByCorp = (corp: CorporationTag, rank: Number): String => {
	switch (corp) {
		case CorporationTag.C_SEC:
			return `img:/corps/${corp}/C_Rank${rank}.png`;
		case CorporationTag.CONFED:
			return `img:/corps/${corp}/D_Rank${rank}.png`;
		case CorporationTag.CREED:
			return `img:/corps/${corp}/${rank}.png`;
		case CorporationTag.FOFT:
			return `img:/corps/${corp}/A_Rank${rank}.png`;
		case CorporationTag.GRAD:
			return `img:/corps/${corp}/${rank}.png`;
		case CorporationTag.GRAD_FAIL:
			return `img:/corps/${corp}/${rank}.png`;
		case CorporationTag.ICE:
			return `img:/corps/${corp}/I_Rank${rank}.png`;
		case CorporationTag.ICU:
			return `img:/corps/${corp}/${rank}.png`;
		case CorporationTag.MERCS:
			return `img:/corps/${corp}/B_Rank${rank}.png`;
		case CorporationTag.NERD:
			return `img:/corps/${corp}/${rank}.png`;
		case CorporationTag.ROBO:
			return `img:/corps/${corp}/${rank}.png`;
		case CorporationTag.SAROS:
			return `img:/corps/${corp}/E_Rank${rank}.png`;
		case CorporationTag.UNI_NAV:
			return `img:/corps/${corp}/${rank}.png`;
		case CorporationTag.XERO:
			return `img:/corps/${corp}/${rank}.png`;
		default:
			return '❓';
	}
};
